import { Download, MoreVert, Print, Share } from "@mui/icons-material";
import { CircularProgress, IconButton, Menu, MenuItem } from "@mui/material";
import { Box } from "@mui/system";
import { useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

interface FileActionsMenuProps {
  fileName: string;
  isLoading?: boolean;
  menuId?: string;
  onPrint?: () => void;
  onDownload: () => void;
  onShare?: () => void;
}

const FileActionsMenu: React.FC<FileActionsMenuProps> = ({
  fileName,
  isLoading = false,
  menuId,
  onPrint,
  onDownload,
  onShare,
}) => {
  const { colors } = useCelleraTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const sanitizedFileName = fileName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  const resolvedMenuId =
    menuId ?? `${sanitizedFileName || "file"}-actions-menu`;
  const isOpen = !!anchorEl;

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        size="small"
        disabled={isLoading}
        aria-label={`Actions for ${fileName}`}
        aria-controls={isOpen ? resolvedMenuId : undefined}
        aria-expanded={isOpen ? "true" : undefined}
        aria-haspopup="menu"
        sx={{
          color: colors.text.secondary,
          flexShrink: 0,
          "&:hover": {
            bgcolor: colors.background.default,
          },
          "&:disabled": {
            opacity: 0.7,
          },
        }}
        title={isLoading ? "Preparing file..." : "File actions"}
      >
        {isLoading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <MoreVert fontSize="small" />
        )}
      </IconButton>

      <Menu
        id={resolvedMenuId}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: { sx: { borderRadius: "8px", minWidth: 160 } },
        }}
      >
        {onPrint && (
          <MenuItem
            onClick={() => {
              handleClose();
              onPrint();
            }}
            disabled={isLoading}
            sx={{ gap: 1.5 }}
          >
            {isLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <Print fontSize="small" />
            )}
            Print file
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            handleClose();
            onDownload();
          }}
          disabled={isLoading}
          sx={{ gap: 1.5 }}
        >
          {isLoading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <Download fontSize="small" />
          )}
          Download file
        </MenuItem>

        {onShare && (
          <MenuItem
            onClick={() => {
              handleClose();
              onShare();
            }}
            disabled={isLoading}
            sx={{ gap: 1.5 }}
          >
            {isLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <Share fontSize="small" />
            )}
            Share file
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default FileActionsMenu;
